import React, {useEffect, useRef, useState} from 'react';
import { useLocation } from 'react-router-dom';
import { useLoading } from '../../context';
import {
  HeroLogo,
  Footer,
  Button,
  Label,
  TextInput,
  FormItemContainer,
  FormContainer, SingleLineFormItemWrapper,
} from '../../components';
import CreditCardPaymentImage from '../../resource/figma-images/credit-card-payment-image.svg';
import { TillPaymentInlineForm } from './TillPaymentInlineForm';
import { Alert, Stack, styled } from '@mui/material';
import ReCAPTCHA from "react-google-recaptcha";
import {
  getCreditCardPaymentImage,
  getDefaultPaymentMethod,
  getPageHeroLogo,
  getPageTitle,
  getThemeColor,
  shouldVerifyRecaptcha
} from "../../customHostConfigOnClient";
import {useRecaptchaReady, verifyRecaptcha} from "../../utils/useRecaptchaReady";
import { PAYMENT_METHOD_CREDIT_CARD, PAYMENT_METHOD_INITIAL } from "../constants";
import {PrivacyPolicy} from "../../components/PrivacyPolicy";
import {
  PaymentDetails,
  PaymentHiddenWrapper,
  PaymentOption,
  PaymentOptionImage,
  PaymentOptionList,
  PaymentOptionText,
} from "../../components/PaymentMethod";

const InfoMessage = styled('div')`
  color: darkgray;
  text-align: center;
`;

const PaymentFormPageTitle = styled('div')`
  font-size: 15px;
  text-align: center;
  font-weight: 500;
  color: #747F94;
`;

export const HMPaymentForm = () => {
  const { showLoading, hideLoading } = useLoading();

  // Hide /hm-pay page for other domains.
  if (window.location.hostname !== 'hm.oligo.com.au' && window.location.hostname !== 'localhost') {
    window.location = '/' + window.location.search;
  }

  const searchParams = new URLSearchParams(useLocation().search);
  const [billAmount, setBillAmount] = useState(searchParams.get('XeroAmount') || '');
  const [billAmountDisabled, setBillAmountDisabled] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [error, setError] = useState('');
  const [paymentResult, setPaymentResult] = useState('');
  const [billAmountError, setBillAmountError] = useState(false);
  const [paymentReference, setPaymentReference] = useState(searchParams.get('XeroInvoiceNo') || '');
  const [paymentReferenceError, setPaymentReferenceError] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerNameError, setCustomerNameError] = useState(false);
  const [userContactPhone, setUserContactPhone] = useState('');
  const tillPaymentInlineFormRef = useRef(null);
  const recaptchaRef = useRef(null);
  const recaptchaReady = useRecaptchaReady();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(getDefaultPaymentMethod() || PAYMENT_METHOD_INITIAL);

  useEffect(() => {
    if (paymentResult !== '') setSelectedPaymentMethod(PAYMENT_METHOD_INITIAL);
  }, [paymentResult]);

  const handlePaymentMethodChange =
    (paymentMethod) => () => {
      setSelectedPaymentMethod(prev => (prev !== paymentMethod ? paymentMethod : prev));
    };

  const envParam = new URLSearchParams(useLocation().search).get('env');

  const handleBillAmountChange = (value) => {
    if (isValidFloat(value)) {
      setBillAmount(value);
      setBillAmountError(false);
    }
  };

  const handlePaymentReferenceChange = (value) => {
    setPaymentReference(value);
    setPaymentReferenceError(false);
  };

  const handleCustomerNameChange = (value) => {
    setCustomerName(value);
    setCustomerNameError(false);
  };

  const isValidFloat = (value) => {
    return /^-?\d*\.?\d*$/.test(value);
  };

  const createPaymentOrderAndNavigate = async () => {
    if (!billAmount || isNaN(parseFloat(billAmount)) || parseFloat(billAmount) <= 0) {
      setBillAmountError(true);
      return;
    }
    if (!customerName) {
      setCustomerNameError(true);
      return;
    }
    if (!paymentReference) {
      setPaymentReferenceError(true);
      return;
    }
    setBillAmountDisabled(true);
    if (selectedPaymentMethod === PAYMENT_METHOD_INITIAL) {
      setError('Please select a payment method.');
      return;
    }
    if (shouldVerifyRecaptcha() && recaptchaReady && recaptchaRef.current) {
      await verifyRecaptcha(recaptchaRef, setError);
    }
    const extraPaymentParams = {
      extraParams: {
        EFN: customerName, // EXTRA_FamilyName
        ECP: userContactPhone, // EXTRA_ContactPhone
      }
    }
    try {
      if (selectedPaymentMethod === PAYMENT_METHOD_CREDIT_CARD) {
        await tillPaymentInlineFormRef.current.paymentSubmit(setPaymentResult, '/api/alipay-credit-card/create-payment-with-token', extraPaymentParams);
      } else {
        setError('Unsupported payment method.');
      }
    } catch (err) {
      hideLoading();
      setError('Create payment order failed, please try again.');
    }
  };

  const handlePayBill = async () => {
    try {
      await createPaymentOrderAndNavigate();
    } catch (err) {
      setError('Payment validation request sending failed');
      console.error(err);
    }
  };

  return (
    <FormContainer>
      <HeroLogo logo={getPageHeroLogo()} redirectPath='/hm-pay' />
      <Stack gap="20px" alignItems="center">
        <PaymentFormPageTitle>{ getPageTitle() }</PaymentFormPageTitle>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack gap="20px" width="100%">
          <SingleLineFormItemWrapper>
            <FormItemContainer marginRight={'10px'}>
              <Label isRequired>Customer Name</Label>
              <TextInput
                placeholder=""
                value={customerName || ''}
                onChange={handleCustomerNameChange}
                error={customerNameError}
              />
            </FormItemContainer>
            <FormItemContainer>
              <Label isRequired>Job ID / Payment Reference</Label>
              <TextInput
                placeholder=""
                value={paymentReference || ''}
                onChange={handlePaymentReferenceChange}
                error={paymentReferenceError}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>
          <SingleLineFormItemWrapper>
            <FormItemContainer marginRight={'10px'}>
              <Label isRequired>Payment Amount</Label>
              <TextInput
                placeholder="$"
                value={billAmount || ''}
                onChange={handleBillAmountChange}
                error={billAmountError}
                disabled={billAmountDisabled}
              />
            </FormItemContainer>
            <FormItemContainer>
              <Label>Contact Telephone No.</Label>
              <TextInput
                placeholder=""
                value={userContactPhone || ''}
                onChange={setUserContactPhone}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>
          <SingleLineFormItemWrapper>
            <FormItemContainer>
              <Label>Receipt Email</Label>
              <TextInput
                placeholder=""
                value={userEmail || ''}
                onChange={setUserEmail}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>

          <div>
            <Label>Payment Method</Label>
            <PaymentOptionList>
              <PaymentOption
                onClick={handlePaymentMethodChange(PAYMENT_METHOD_CREDIT_CARD)}
                selected={selectedPaymentMethod === PAYMENT_METHOD_CREDIT_CARD}
                borderColor={getThemeColor()}
              >
                <PaymentOptionImage src={CreditCardPaymentImage} />
                <PaymentOptionText>Card</PaymentOptionText>
              </PaymentOption>
            </PaymentOptionList>
            <PaymentDetails>
              <PaymentHiddenWrapper show={selectedPaymentMethod === PAYMENT_METHOD_CREDIT_CARD}>
                <TillPaymentInlineForm ref={tillPaymentInlineFormRef}
                                       billAmount={billAmount}
                                       billReference={paymentReference}
                                       userEmail={userEmail}
                                       initLoadingFinishCallback={() => getDefaultPaymentMethod() && setSelectedPaymentMethod(getDefaultPaymentMethod())}
                                       creditCardImage={getCreditCardPaymentImage()}
                />
              </PaymentHiddenWrapper>
            </PaymentDetails>
            { paymentResult && <InfoMessage>{paymentResult}</InfoMessage> }
            { shouldVerifyRecaptcha() && <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6LcWNAUrAAAAAHX2haltRqZqMAMZ0qB29zv16EB8"
              size="invisible"
            /> }
            <Button $themeColor={getThemeColor()} onClick={handlePayBill} block>
              Continue to pay
            </Button>
          </div>
        </Stack>
      </Stack>
      <PrivacyPolicy />
      <Footer />
    </FormContainer>
  );
};
